import type { WelcomeCopyKey } from '@/i18n/ns/welcome';

// Device-local presentation preferences, never authority or connection state.
// The server's membership-scoped welcome completion remains authoritative.
export type Focus = 'everyday' | 'commerce' | 'finance' | 'projects';
export type Role =
  | 'leadership'
  | 'product'
  | 'engineering'
  | 'marketing'
  | 'sales'
  | 'operations'
  | 'finance';
export type SetupPreferences = {
  step: 0 | 1 | 2;
  interests: Focus[];
  role: Role | null;
  dismissed: boolean;
};
// Labels are keys into the 24-language `welcome` namespace (ui/i18n/ns/welcome.ts).
export const roles: { id: Role; label: WelcomeCopyKey }[] = [
  { id: 'leadership', label: 'roleLeadership' },
  { id: 'product', label: 'roleProduct' },
  { id: 'engineering', label: 'roleEngineering' },
  { id: 'marketing', label: 'roleMarketing' },
  { id: 'sales', label: 'roleSales' },
  { id: 'operations', label: 'roleOperations' },
  { id: 'finance', label: 'roleFinance' },
];
const roleFocus: Record<Role, Focus> = {
  leadership: 'everyday',
  product: 'projects',
  engineering: 'projects',
  marketing: 'commerce',
  sales: 'commerce',
  operations: 'everyday',
  finance: 'finance',
};
export function suggestedFocuses(interests: readonly Focus[], role: Role | null): Focus[] {
  return [...new Set([...interests, ...(role ? [roleFocus[role]] : [])])];
}
export const focuses: { id: Focus; title: WelcomeCopyKey; description: WelcomeCopyKey }[] = [
  { id: 'everyday', title: 'goalEverydayTitle', description: 'goalEverydayBody' },
  { id: 'commerce', title: 'goalCommerceTitle', description: 'goalCommerceBody' },
  { id: 'finance', title: 'goalFinanceTitle', description: 'goalFinanceBody' },
  { id: 'projects', title: 'goalProjectsTitle', description: 'goalProjectsBody' },
];
export const setupKey = (organizationId: string, userId: string) =>
  `poneva.setup.v1:${encodeURIComponent(organizationId)}:${encodeURIComponent(userId)}`;
export function readSetup(key: string): SetupPreferences {
  const fallback: SetupPreferences = { step: 0, interests: [], role: null, dismissed: false };
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (!value || typeof value !== 'object') return fallback;
    // Keep existing members' progress when migrating the former single choice.
    const selected = Array.isArray(value.interests) ? value.interests : [value.focus];
    return {
      interests: focuses.filter((entry) => selected.includes(entry.id)).map((entry) => entry.id),
      role: roles.find((entry) => entry.id === value.role)?.id ?? null,
      step: value.step === 1 || value.step === 2 ? value.step : 0,
      dismissed: value.dismissed === true,
    };
  } catch {
    return fallback;
  }
}
export function saveSetup(key: string, value: SetupPreferences) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Setup also works without storage. */
  }
}
